import { useMemo } from "react";
import { Alert, Box, Chip, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import GetAppIcon from "@mui/icons-material/GetApp";
import { formatKotDateTime } from "../../utils/dateHelpers";
import TruncatedText from "../../components/TruncatedText";

const NoUploadsOverlay = () => (
  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
    <Typography>No inventor uploads yet</Typography>
  </Box>
);

const FILE_ERROR = "FILE_ERROR";

/** A batch the server is still working through has no counts to judge it by yet. */
const IN_PROGRESS_STATUSES = ["PENDING", "PROCESSING", "IN_PROGRESS"];

/**
 * How an upload as a whole turned out, from its counts. Distinct from a row's validation
 * status: a batch is "Partially Imported" when some rows landed and others didn't, whether
 * those others were Partial Success or Failed.
 *
 * Status is checked before the counts, because a batch still running and a file that held
 * no rows both have zero of everything and neither of them failed.
 */
const batchOutcome = (batch) => {
  if (batch.status === FILE_ERROR) return { label: "Failed", color: "error" };
  if (IN_PROGRESS_STATUSES.includes(batch.status)) return { label: "In Progress", color: "default" };

  // Matches what the upload summary says about the same batch.
  if ((batch.totalRows || 0) === 0) return { label: "No Rows", color: "default" };

  const imported = (batch.successCount || 0) + (batch.partialCount || 0);
  if (imported === 0) return { label: "Failed", color: "error" };
  if ((batch.failedCount || 0) > 0 || (batch.partialCount || 0) > 0) {
    return { label: "Partially Imported", color: "warning" };
  }
  return { label: "Completed", color: "success" };
};

/**
 * The audit trail of this client's past uploads (KOT-109): who uploaded what, when, and
 * how it turned out. Rejected files are listed too, so a wrong-template attempt is visible
 * rather than silently absent.
 *
 * No Client No. column: every row belongs to the client whose section this is.
 *
 * Column widths are deliberately tight. Their sum plus the two flexible columns' minimums
 * is what decides whether the grid scrolls sideways, and a horizontal scrollbar here hides
 * the download button - the one thing people open this table to reach. Fixed 795 plus
 * flexible minimums 340 = 1135, which fits the page's content box down to a ~1280px
 * screen.
 */
const UploadHistory = ({ batches, loading, error, downloadError, onDownloadResult, downloadingBatchId }) => {
  // Memoised because MUI's DataGrid treats a new `columns` identity as a column definition
  // change and remeasures all nine, discarding any resizing the user has done - and this
  // component re-renders on every loading and download tick.
  const columns = useMemo(() => [
    {
      field: "uploadedAt",
      headerName: "Upload Date",
      width: 165,
      renderCell: (params) => <span>{formatKotDateTime(params.row.uploadedAt)}</span>,
    },
    {
      field: "fileName",
      headerName: "File Name",
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => <TruncatedText text={params.row.fileName} maxLength={40} />,
    },
    { field: "totalRows", headerName: "Total Records", width: 120 },
    { field: "successCount", headerName: "Success", width: 90 },
    { field: "partialCount", headerName: "Partial", width: 85 },
    { field: "failedCount", headerName: "Failed", width: 85 },
    {
      field: "status",
      headerName: "Status",
      // Wide enough for the "Partially Imported" chip, which is the longest label.
      width: 160,
      renderCell: (params) => {
        const { label, color } = batchOutcome(params.row);
        return params.row.status === FILE_ERROR ? (
          <Tooltip title={params.row.errorMessage || "The file was rejected"}>
            <Chip size="small" color={color} label={label} />
          </Tooltip>
        ) : (
          <Chip size="small" color={color} label={label} />
        );
      },
    },
    {
      field: "uploadedBy",
      headerName: "Uploaded By",
      flex: 1,
      minWidth: 140,
      sortable: false,
      // The API resolves the person id to a name; older rows may still be bare ids.
      valueGetter: (params) => params.row.uploadedBy?.displayName || "Unknown",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 90,
      sortable: false,
      renderCell: (params) =>
        // A rejected file never produced result rows, so there is nothing to download.
        params.row.status === FILE_ERROR ? (
          <Typography variant="caption" color="text.secondary">
            Not available
          </Typography>
        ) : (
          <IconButton
            size="small"
            aria-label={`Download validation result for ${params.row.fileName}`}
            disabled={downloadingBatchId === params.row.batchId}
            onClick={() => onDownloadResult(params.row.batchId)}
          >
            {downloadingBatchId === params.row.batchId ? <CircularProgress size={18} /> : <GetAppIcon />}
          </IconButton>
        ),
    },
  ], [onDownloadResult, downloadingBatchId]);

  return (
    <Box data-testid="inventor-bulk-upload-history">
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
          {error}
        </Alert>
      )}

      {downloadError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
          {downloadError}
        </Alert>
      )}

      <Box sx={{ height: 420, width: "100%", background: "#fff", border: "1px solid #ddd", borderRadius: 2 }}>
        <DataGrid
          rows={batches}
          columns={columns}
          getRowId={(row) => row.batchId}
          loading={loading}
          slots={{ noRowsOverlay: NoUploadsOverlay }}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            // Newest first: the batch people come here to check is almost always the one
            // they just uploaded, and insertion order would bury it on the last page.
            sorting: { sortModel: [{ field: "uploadedAt", sort: "desc" }] },
          }}
          pageSizeOptions={[10, 25, 50]}
          sx={{
            border: 0,
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f7fb",
              fontWeight: "bold",
              borderBottom: "1px solid #ddd",
            },
            "& .MuiDataGrid-cell": { borderBottom: "1px solid #eee", overflow: "hidden" },
            "& .MuiDataGrid-columnSeparator": { visibility: "visible" },
            ".MuiTablePagination-displayedRows": { marginTop: "1em", marginBottom: "1em" },
            ".MuiTablePagination-selectLabel": { marginTop: "1em", marginBottom: "1em" },
          }}
        />
      </Box>
    </Box>
  );
};

export default UploadHistory;
